import {
    LabeledComponentDefinition,
    ValueComponentDefinition,
} from "components/components/Component";
import { OutputHTMLElement } from "components/output/baseHTMLElementOutput";
import { Tooltip } from "components/components/tooltip/tooltip";
import { fireDataChangedEvent } from "components/form/dataChangedEventDetail";
import { FocusManager } from "components/pages/focusManager";
import { ValueBaseComponent } from "components/components/ValueBaseComponent";
import isEmpty from "lodash/isEmpty";
import trim from "lodash/trim";
import "./extraOutputCSS.scss";

export abstract class OutputBaseComponent<U extends OutputBaseDefinition> extends ValueBaseComponent<U> {

    constructor(schema: U) {
        super(schema);
        if (schema.labelKey == null || schema.labelKey.length === 0) {
            this.definition.labelWidth = 0;
        }
        this.definition.emptyWidth = this.definition.emptyWidth ?? 0;
        if (this.definition.inputWidth == null) {
            this.definition.inputWidth = 16 - (this.definition.labelWidth || 0) - this.definition.emptyWidth;
        }
        this.definition.width = this.definition.width ?? 16;
        this.definition.labelWidth = this.definition.labelWidth ?? 16;
    }

    public renderTemplate(template: any, params?: any): Element {
        let objTemplate: any = Object.assign(
            this.definition,
            params ?? {},
        );
        let jqueryView = $(template(objTemplate));
        this.view = jqueryView[0];
        return this.view;
    }

    public tooltipBindElement(selectors: string) {
        Tooltip.bindElement(this.view.querySelector(selectors));
    }

    public genericBindEventsOutputHTMLElement(element: OutputHTMLElement): void {
        if (element != null && this.view != null) {
            element.addEventListener("change", () => {
                const data = element.data;
                const isEqual = element.isEqualToInitialValue(data);
                if (!isEqual) {
                    element.initialValue.reassignvalue(data);
                    fireDataChangedEvent(this, this.view, data);
                }
            });
        }
    }
}

export abstract class SimpleOutputBaseComponent<U extends SimpleOutputBaseDefinition> extends OutputBaseComponent<U> {

    constructor(schema: U) {
        super(schema);
    }

    public genericBindEvents(element: OutputHTMLElement): void {
        this.genericBindEventsHTMLElement(element);
        this.genericBindEventsOutputHTMLElement(element);
    }

    public genericBindEventsHTMLElement(element: HTMLElement): void {
        if (element != null) {
            if (this.definition.tabOnEnter === true) {
                element.addEventListener("keydown", (e) => {
                    if (e.key === "Enter") {
                        e.stopPropagation();
                        e.preventDefault();
                        FocusManager.focusNext();
                    }
                });
            }
        }
    }

    public generateSytleAttribute(): any {
        let objStyle: any = {};
        let styleCSSOuput = this.getDefinitionStyleOutputCss();
        if (styleCSSOuput) {
            objStyle.styleCSSOutput = styleCSSOuput;
        }

        let classCSSOutput = this.getDefinitionClassOutputCss();
        if (classCSSOutput) {
            objStyle.classCSSOutput = classCSSOutput;
        }
        return objStyle;
    }

    private getDefinitionStyleOutputCss(): string {
        let styleCss = null;
        if (!isEmpty(this.definition.styleCSS)) {
            styleCss = "";
            styleCss += this.styleCss('maxWidth', 'max-width: calc({0} + 15px);');
            styleCss += this.styleCss('flexBasis', 'flex-basis: calc({0} + 15px);');
            styleCss += this.styleCss('flexGrow', 'flex-grow: {0};');
            styleCss += this.styleCss('textAlign', 'text-align: {0};');
            styleCss = trim(styleCss);
        }

        return styleCss;
    }

    private styleCss(property: string, styleProperty: string): string {
        let tmp = "";
        if (!isEmpty(this.definition.styleCSS[property])) {
            tmp = this.formatString(styleProperty, this.definition.styleCSS[property]);
        }
        return tmp;
    }

    private formatString(str: string, ...val: string[]) {
        for (let index = 0; index < val.length; index++) {
            str = str.replace(`{${index}}`, val[index]);
        }
        return str;
    }

    private getDefinitionClassOutputCss(): string {

        let extraClassCss = null;

        if (!isEmpty(this.definition.styleClassCSS)) {
            extraClassCss = "";
            extraClassCss += this.classCss('border');
            extraClassCss += this.classCss('background');
            extraClassCss += this.classCss('font');
            extraClassCss = trim(extraClassCss);
        }
        return extraClassCss;
    }


    private classCss(property: string): string {
        let tmp = "";
        if (!isEmpty(this.definition.styleClassCSS[property])) {
            //class name must have blank space between them
            tmp = " " + this.definition.styleClassCSS[property];
        }
        return tmp;
    }
}

export abstract class DateOutputBaseComponent<U extends SimpleOutputBaseDefinition> extends SimpleOutputBaseComponent<U> {

    constructor(schema: U) {
        super(schema);
    }

    public renderTemplate(template: any, params?: any): Element {
        let d = document.createElement("div");
        d.innerHTML = template(Object.assign(
            this.definition, params ?? {}
        ));
        this.view = d.firstChild as HTMLElement;
        return this.view;
    }
}

export interface OutputBaseDefinition extends LabeledComponentDefinition, ValueComponentDefinition {
    inputWidth?: number;
    emptyWidth?: number;
}

export interface SimpleOutputBaseDefinition extends OutputBaseDefinition {
    tabOnEnter?: boolean;
    disableFocusKeyboard?: boolean;
    styleCSS?: StyleCSSOuput;
    styleClassCSS?: StyleClassCSSOuput;
}

export interface StyleCSSOuput {
    maxWidth?: string;
    flexBasis?: string;
    flexGrow?: number;
    textAlign?: string;
}

export interface StyleClassCSSOuput {
    border?: borderClassCSSOutput;
    background?: backgroundClassCSSOutput;
    font?: fontClassCSSOutput;
}

enum borderClassCSSOutput {
    b_cerise,
    b_fungreen,
    b_forest,
    b_tundora
}

enum backgroundClassCSSOutput {
    bk_cerise,
    bk_fungreen,
    bk_forest,
    bk_tundora
}

enum fontClassCSSOutput {
    f_cerise,
    f_fungreen,
    f_forest,
    f_tundora
}
